package com.bikompanion.ui.home

import androidx.compose.animation.core.spring
import androidx.compose.foundation.layout.padding
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.State
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.runtime.withFrameNanos
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.drop
import kotlinx.coroutines.flow.filterIsInstance
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import org.maplibre.compose.camera.CameraAnimation
import org.maplibre.compose.camera.CameraMoveReason
import org.maplibre.compose.camera.CameraPosition
import org.maplibre.compose.camera.CameraUpdate
import org.maplibre.compose.location.LocationPermission
import org.maplibre.compose.location.LocationState
import org.maplibre.compose.map.MapState
import org.maplibre.spatialk.geojson.Position
import org.maplibre.spatialk.units.Bearing
import org.maplibre.spatialk.units.extensions.inDegrees
import com.bikompanion.ui.models.LocateMode
import kotlin.math.abs
import kotlin.math.pow
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.nanoseconds
import kotlin.time.Duration.Companion.seconds

// Neither wait has a state that says "the user said no" — a denied permission and an
// unanswered dialog look the same — so both are bounded rather than left hanging.
private val PermissionAnswerTimeout = 60.seconds

private val FirstFixTimeout = 20.seconds

// How long each filter below takes to close half the gap to the newest reading. Both run per
// frame rather than as animations with a settling time, so what they cost is a fixed lag and
// nothing else — no acceleration at the start of a reading and no deceleration at the end, which
// is what makes a turn read as one movement rather than a sequence of them.
//
// The heading's is short: a reading is already the direction the device points, so all this has
// to swallow is sensor wobble. The position's is longer, because it bridges whole seconds
// between GPS fixes and a fix that lands a few metres off should drift the map, not snap it.
private val HeadingHalfLife = 80.milliseconds

private val FollowPositionHalfLife = 250.milliseconds

// Below these the filter has arrived: it snaps to the reading and its frame loop parks until the
// next one. An exponential never lands on its own, and without a stop the camera would be
// rewritten every frame forever — which leaves the map permanently "moving" and starves
// everything that waits for the viewport to settle. A twentieth of a degree is far below what a
// turn shows, and 1e-7 degrees of arc is about a centimetre.
private const val HeadingSettleDegrees = 0.05f

private const val PositionSettleDegrees = 1e-7

// Looking along the ground rather than down at it — the same tilt a phone in a hand held for
// walking gives, and what turns "a map that spins" into "the road ahead".
private const val FollowTiltDegrees = 45.0

private val FollowTiltDuration = 600.milliseconds

/**
 * The direction the rider is facing, in degrees clockwise from north, animated between readings.
 *
 * Null until there is something to point at — neither a compass reading nor, on a platform with
 * no compass, a course to fall back on.
 *
 * The readings themselves are steps: even sampled twenty times a second they are a sequence of
 * positions, not a movement, and drawn straight they judder. What is exposed here is a filter
 * chasing the latest reading once per frame, so it has a fresh value on every frame whether a
 * reading arrived or not.
 *
 * A per-frame filter rather than a spring per reading. A spring restarted every 50 ms spends its
 * whole life in its own slow start, so the map lagged the device by most of a second and moved
 * in the steps the restarts cut it into. This closes a fixed fraction of the remaining gap each
 * frame instead, which has one lag and no shape of its own.
 *
 * Read through a [State] rather than returned as a value: this changes continuously, and
 * anything that read it during composition would recompose the whole screen at frame rate.
 */
@Composable
internal fun rememberSmoothedHeading(locationState: LocationState): State<Float?> {
    val smoothed = remember { mutableFloatStateOf(0f) }
    val hasReading = remember { mutableStateOf(false) }
    val reading = remember(locationState) { mutableStateOf<Float?>(null) }
    LaunchedEffect(locationState) {
        // The compass wins whenever there is one, and the course is only a fallback for a
        // platform without one. Picking by reported accuracy instead — which is what the
        // library's own `mostAccurateBearing` does — hands the map to the course as soon as the
        // rider moves, and a compass whose accuracy the sensor declines to report loses to it
        // outright: turning the phone then did nothing at all, which is exactly what was seen.
        coroutineScope {
            launch {
                snapshotFlow {
                    locationState.lastHeading?.bearing
                        ?: locationState.lastLocation?.course
                }.filterNotNull()
                    .collect { reading.value = (it - Bearing.North).inDegrees.toFloat() }
            }
            while (true) {
                // Parked until there is a gap worth closing, so a phone held still costs no
                // frame callbacks at all.
                val first = snapshotFlow {
                    reading.value?.takeIf {
                        !hasReading.value || abs(shortWay(it - smoothed.floatValue)) >= HeadingSettleDegrees
                    }
                }.filterNotNull().first()
                if (!hasReading.value) {
                    smoothed.floatValue = first.mod(360f)
                    hasReading.value = true
                    continue
                }
                var lastFrame = withFrameNanos { it }
                while (true) {
                    val now = withFrameNanos { it }
                    val target = reading.value ?: break
                    // Wrapped to the short way round: told to go from 359 to 1 the filter would
                    // otherwise travel the 358 degrees in between, and the map would spin most
                    // of a full turn to answer a step across north.
                    val delta = shortWay(target - smoothed.floatValue)
                    if (abs(delta) < HeadingSettleDegrees) {
                        smoothed.floatValue = target.mod(360f)
                        break
                    }
                    val elapsed = (now - lastFrame).nanoseconds
                    lastFrame = now
                    smoothed.floatValue =
                        (smoothed.floatValue + delta * catchUp(elapsed, HeadingHalfLife)).mod(360f)
                }
            }
        }
    }
    return remember { derivedStateOf { if (hasReading.value) smoothed.floatValue else null } }
}

/**
 * The fraction of the remaining gap a filter with this [halfLife] closes over [elapsed].
 *
 * Derived from the frame's own length rather than assumed, so the same filter behaves identically
 * on a 60 Hz panel, a 120 Hz one, and the long frame after a dropped one.
 */
private fun catchUp(
    elapsed: Duration,
    halfLife: Duration,
): Float {
    if (elapsed <= Duration.ZERO) return 0f
    // A frame this long means the loop was descheduled, not that the map should leap; capped so
    // it resumes from where it is instead.
    val steps = (elapsed / halfLife).coerceAtMost(4.0)
    return (1.0 - 2.0.pow(-steps)).toFloat()
}

/** [degrees] as a turn of at most half a circle, in whichever direction is shorter. */
private fun shortWay(degrees: Float): Float = ((degrees + 540f) % 360f) - 180f

/**
 * Keeps the camera on the rider while a locate mode is on, one frame at a time.
 *
 * The camera is written directly rather than handed to the map as an animation. `animateTo` is
 * MapLibre's cinematic fly, which eases in and out of every call it is given; asked for one of
 * those per compass reading, the map spent each of them accelerating, was cancelled before it
 * reached speed, and started over — a turn that arrived both late and in visible steps. Here the
 * bearing already arrives smoothed frame by frame (see [rememberSmoothedHeading]) and the
 * position is filtered the same way, so there is nothing left for the map to interpolate: each
 * frame carries the transform that frame should show.
 *
 * The loop parks between moves rather than writing every frame regardless — see the note on
 * [HeadingSettleDegrees] for why that matters beyond the wasted work.
 */
@Composable
internal fun FollowCameraEffect(
    locateMode: LocateMode,
    locationState: LocationState,
    heading: () -> Float?,
    cameraState: MapState,
) {
    val currentHeading by rememberUpdatedState(heading)
    LaunchedEffect(locateMode, locationState, cameraState) {
        if (locateMode == LocateMode.Off) return@LaunchedEffect
        val following = locateMode == LocateMode.FollowingHeading
        // The tilt is a one-off on the way in, not part of the following, so it keeps its own
        // animation — nothing is chasing a reading here, so the fly's easing is what is wanted.
        // Centring leaves the tilt alone: a rider who tilted the map by hand and then asked to
        // be centred did not ask to have it flattened.
        if (following) {
            cameraState.animateCamera(
                CameraUpdate(pitch = FollowTiltDegrees),
                CameraAnimation.Ease(from = CameraAnimation.Ease.Standard) { duration = FollowTiltDuration },
            )
        }
        // Whether the camera is anywhere other than where this loop wants it. Writing an
        // unchanged camera still counts as a camera move to everything watching one, so a rider
        // standing still would otherwise keep the map reporting motion for as long as the mode
        // is on — and cost a frame callback a frame to do it.
        //
        // The bearing is compared as an angle rather than a number: the camera read here is the
        // one the map adopted and reported back, and it normalises the range it answers in, so
        // the 350 this loop wrote can come back as -10. Compared as numbers those never agree,
        // and the loop would write the same bearing on every frame forever.
        val bearingSettled = { current: CameraPosition ->
            val wanted = currentHeading()
            wanted == null || abs(shortWay(wanted - current.bearing.toFloat())) < HeadingSettleDegrees
        }
        val offTarget = {
            val current = cameraState.cameraPosition
            val fix = locationState.lastLocation?.position
            (fix != null && !fix.isWithin(current.center)) ||
                (following && !bearingSettled(current))
        }
        while (true) {
            snapshotFlow(offTarget).first { it }
            var lastFrame = withFrameNanos { it }
            while (true) {
                val now = withFrameNanos { it }
                val elapsed = (now - lastFrame).nanoseconds
                lastFrame = now
                // Zoom, tilt and padding are read back from the camera every frame: they are the
                // properties this loop does not own, and a pinch or the tilt animation above
                // changes them underneath it.
                val current = cameraState.cameraPosition
                val fix = locationState.lastLocation?.position
                val target = when {
                    fix == null -> current.center
                    else -> current.center.towards(fix, catchUp(elapsed, FollowPositionHalfLife))
                }
                val bearing = when {
                    !following -> current.bearing
                    else -> currentHeading()?.toDouble() ?: current.bearing
                }
                if (target.isWithin(current.center) && (!following || bearingSettled(current))) break
                cameraState.setCameraPosition(current.copy(center = target, bearing = bearing))
            }
        }
    }
}

/** This position moved [fraction] of the way to [other]. */
private fun Position.towards(
    other: Position,
    fraction: Float,
): Position {
    if (isWithin(other)) return other
    return Position(
        longitude = longitude + (other.longitude - longitude) * fraction,
        latitude = latitude + (other.latitude - latitude) * fraction,
    )
}

/** Whether [other] is close enough to this position that no camera could tell them apart. */
private fun Position.isWithin(other: Position): Boolean =
    abs(other.longitude - longitude) < PositionSettleDegrees &&
        abs(other.latitude - latitude) < PositionSettleDegrees

@Composable
internal fun StopFollowingOnGestureEffect(
    locateMode: LocateMode,
    cameraState: MapState,
    onGesture: () -> Unit,
) {
    val currentOnGesture by rememberUpdatedState(onGesture)
    // A pan, pinch or rotate is the user saying which ground they want to look at, so tracking
    // stops rather than dragging the camera back on the next fix. Only gestures count: the
    // effect above moves the camera itself, and treating that as a reason to stop would end
    // tracking on the first fix it delivered.
    LaunchedEffect(locateMode) {
        if (locateMode == LocateMode.Off) return@LaunchedEffect
        // The reason is written once, when a move starts, and then left alone — so it is
        // whatever the last move was, gesture or not, at the moment tracking is switched on.
        // Dropping that first reading is what stops the pan that preceded the tap from
        // cancelling the mode it just asked for. `isCameraMoving` is read alongside it because
        // two gestures in a row leave the reason unchanged; the flag is what marks the second
        // one as a new move rather than the one already accounted for.
        snapshotFlow { cameraState.cameraMoveReason to cameraState.isCameraMoving }
            .drop(1)
            .first { (reason, moving) -> reason == CameraMoveReason.Gesture && moving }
        currentOnGesture()
    }
}

/** Gets a position, asking for permission if it has to, and centres on it; false if none came. */
internal suspend fun flyToRider(
    locationState: LocationState,
    cameraState: MapState,
): Boolean {
    if (locationState.permission !is LocationPermission.Granted) {
        // Returns as soon as the dialog is up; the answer only reaches `permission` once the user
        // has given one. That is why a tap used to do nothing but ask, leaving the user to guess
        // that a second tap would centre the map.
        locationState.requestPermission()
        withTimeoutOrNull(PermissionAnswerTimeout) {
            snapshotFlow { locationState.permission }
                .filterIsInstance<LocationPermission.Granted>()
                .first()
        } ?: return false // Denied, or left unanswered.
    }
    // A first fix takes a few seconds, and it can only start once permission is in hand — so on
    // the tap that granted it, there is nothing to centre on yet.
    val location = withTimeoutOrNull(FirstFixTimeout) {
        snapshotFlow { locationState.lastLocation }.filterNotNull().first()
    } ?: return false
    cameraState.animateCamera(
        CameraUpdate(center = location.position, zoom = 16.0),
        CameraAnimation.Fly.Standard,
    )
    return true
}
